// Vendor detail + editor — KYC, contact, business details, commission,
// plan, and admin metadata (flagged/notes/tier). Editorial fields write
// to cms.users; admin metadata writes to app.vendor_profiles. Both happen
// inside the same Hono PATCH transaction.

import { TRANSFERS_UI_ENABLED } from '@/lib/features';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { AvatarPanel } from '@/components/AvatarPanel';
import { DefRow, DetailLayout, DetailSection } from '@drivecars/admin-ui';
import { EntityActivity } from '@/components/EntityActivity';
import { VendorTransfersSection } from './VendorTransfersSection';
import { VendorLoginsSection } from './VendorLoginsSection';
import { VendorSheetSection } from './VendorSheetSection';
import { VendorAvailabilitySection } from './VendorAvailabilitySection';
import { VendorStatementsSection } from './VendorStatementsSection';
import { VendorAutoAssignField } from './VendorAutoAssignField';
import { VendorFixedPriceField } from './VendorFixedPriceField';
import { StatusPill } from '@drivecars/admin-ui';
import { RowAction } from '@/components/RowAction';
import {
  TextField,
  TextAreaField,
  NumberField,
  SelectField,
  BooleanField,
  RelationshipPicker,
  type RelationshipOption,
  type SelectOption,
} from '@/components/forms';
import { env } from '@/lib/env';
import { useNavigate } from '@tanstack/react-router';
import { DataTable, type DataTableColumn } from '@drivecars/admin-ui';
import {
  approveVendorKyc,
  fetchAdminVendorDetail,
  fetchAdminVendorPayouts,
  fetchAdminVendorPlans,
  patchVendor,
  rejectVendorKyc,
  suspendVendor,
  unsuspendVendor,
  type AdminVendorCommissionType,
  type AdminVendorDetail,
  type AdminVendorPayoutRow,
  type AdminVendorStatus,
  type AdminVendorVerificationStatus,
  type VendorPatchInput,
} from '@/lib/admin-api';

function resolveMediaUrl(rel: string | null | undefined): string | null {
  if (!rel) return null;
  if (/^https?:\/\//.test(rel)) return rel;
  return `${env().PAYLOAD_URL}${rel}`;
}

const fmtMoney = (cents: number) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(cents / 100);

const STATUS_OPTIONS: SelectOption<AdminVendorStatus>[] = [
  { value: 'active', label: 'Active' },
  { value: 'pending', label: 'Pending' },
  { value: 'suspended', label: 'Suspended' },
];
const VERIFICATION_OPTIONS: SelectOption<AdminVendorVerificationStatus>[] = [
  { value: 'unverified', label: 'Unverified' },
  { value: 'pending', label: 'Pending review' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];
const COMMISSION_OPTIONS: SelectOption<AdminVendorCommissionType | ''>[] = [
  { value: '', label: '— (use platform default)' },
  { value: 'default', label: 'Default' },
  { value: 'percent', label: 'Percent' },
  { value: 'fixed', label: 'Fixed' },
];

// ─── Editor draft ────────────────────────────────────────────────────

interface VendorDraft {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  status: AdminVendorStatus;
  verificationStatus: AdminVendorVerificationStatus;
  businessName: string;
  businessAddress: string;
  taxId: string;
  commissionType: AdminVendorCommissionType | '';
  commissionValue: number | null;
  // Per-crew pricing overrides as fractions (0.05 = 5%). null = use global default.
  vehicleMarkupPct: number | null;
  platformFeePct: number | null;
  // The rate THIS CREW agreed to pay DriveCars when a partner sells it on a
  // supplier-commission model (0241). null = no rate agreed, which hides the
  // crew from those partners entirely — never the same as 0.
  supplierCommissionPct: number | null;
  notificationChannel: 'whatsapp' | 'email' | 'both';
  whatsappPhone: string;
  // Pickup point for the results map (one pin per vendor).
  pickupLatitude: number | null;
  pickupLongitude: number | null;
  planId: number | null;
  planOption: RelationshipOption | null;
  // Admin metadata (app.vendor_profiles)
  flagged: boolean;
  notes: string;
  tier: string;
  autoAssign: boolean;
}

function vendorToDraft(v: AdminVendorDetail): VendorDraft {
  return {
    email: v.email,
    firstName: v.firstName ?? '',
    lastName: v.lastName ?? '',
    phone: v.phone ?? '',
    status: v.status,
    verificationStatus: v.verificationStatus ?? 'unverified',
    businessName: v.businessName ?? '',
    businessAddress: v.businessAddress ?? '',
    taxId: v.taxId ?? '',
    commissionType: v.commissionType ?? '',
    commissionValue: v.commissionValue,
    vehicleMarkupPct: v.vehicleMarkupPct,
    platformFeePct: v.platformFeePct,
    supplierCommissionPct: v.supplierCommissionPct,
    notificationChannel: v.notificationChannel ?? 'email',
    whatsappPhone: v.whatsappPhone ?? '',
    pickupLatitude: v.pickupLatitude ?? null,
    pickupLongitude: v.pickupLongitude ?? null,
    planId: v.planId,
    planOption: v.planId
      ? { id: v.planId, label: v.planName ?? `Plan #${v.planId}` }
      : null,
    flagged: v.profile?.flagged ?? false,
    notes: v.profile?.notes ?? '',
    tier: v.profile?.tier ?? '',
    autoAssign: v.autoAssign,
  };
}

function buildPatch(v: AdminVendorDetail, d: VendorDraft): VendorPatchInput {
  const p: VendorPatchInput = {};
  if (d.email.trim() !== v.email) p.email = d.email.trim();
  const firstName = d.firstName.trim() === '' ? null : d.firstName.trim();
  if (firstName !== v.firstName) p.firstName = firstName;
  const lastName = d.lastName.trim() === '' ? null : d.lastName.trim();
  if (lastName !== v.lastName) p.lastName = lastName;
  const phone = d.phone.trim() === '' ? null : d.phone.trim();
  if (phone !== v.phone) p.phone = phone;
  if (d.status !== v.status) p.status = d.status;
  const ver = d.verificationStatus;
  if (ver !== (v.verificationStatus ?? 'unverified')) p.verificationStatus = ver;

  const businessName = d.businessName.trim() === '' ? null : d.businessName.trim();
  if (businessName !== v.businessName) p.businessName = businessName;
  const businessAddress =
    d.businessAddress.trim() === '' ? null : d.businessAddress.trim();
  if (businessAddress !== v.businessAddress) p.businessAddress = businessAddress;
  const taxId = d.taxId.trim() === '' ? null : d.taxId.trim();
  if (taxId !== v.taxId) p.taxId = taxId;

  const commissionType = d.commissionType === '' ? null : d.commissionType;
  if (commissionType !== v.commissionType) p.commissionType = commissionType;
  if (d.commissionValue !== v.commissionValue) p.commissionValue = d.commissionValue;
  if (d.vehicleMarkupPct !== v.vehicleMarkupPct) p.vehicleMarkupPct = d.vehicleMarkupPct;
  if (d.platformFeePct !== v.platformFeePct) p.platformFeePct = d.platformFeePct;
  // Sent whenever it CHANGED, including to null: clearing the rate is how ops
  // withdraws a crew from supplier-commission partners, so an omitted null
  // would silently keep selling it.
  if (d.supplierCommissionPct !== v.supplierCommissionPct)
    p.supplierCommissionPct = d.supplierCommissionPct;
  if (d.notificationChannel !== (v.notificationChannel ?? 'email'))
    p.notificationChannel = d.notificationChannel;
  const whatsappPhone = d.whatsappPhone.trim() === '' ? null : d.whatsappPhone.trim();
  if (whatsappPhone !== v.whatsappPhone) p.whatsappPhone = whatsappPhone;
  if (d.pickupLatitude !== (v.pickupLatitude ?? null)) p.pickupLatitude = d.pickupLatitude;
  if (d.pickupLongitude !== (v.pickupLongitude ?? null)) p.pickupLongitude = d.pickupLongitude;
  if (d.planId !== v.planId) p.planId = d.planId;

  // Admin metadata
  if (d.flagged !== (v.profile?.flagged ?? false)) p.flagged = d.flagged;
  const notes = d.notes.trim() === '' ? null : d.notes.trim();
  if (notes !== (v.profile?.notes ?? null)) p.notes = notes;
  const tier = d.tier.trim() === '' ? null : d.tier.trim();
  if (tier !== (v.profile?.tier ?? null)) p.tier = tier;
  if (d.autoAssign !== v.autoAssign) p.autoAssign = d.autoAssign;

  return p;
}

// ─── Page ────────────────────────────────────────────────────────────

export function VendorDetail() {
  const params = useParams({ strict: false }) as { id?: string };
  const id = Number(params.id);
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'vendors', id],
    queryFn: ({ signal }) => fetchAdminVendorDetail(id, signal),
  });

  const [draft, setDraft] = useState<VendorDraft | null>(null);
  useEffect(() => {
    if (data) setDraft(vendorToDraft(data));
  }, [data]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'vendors'] });
  };

  const approveMut = useMutation({ mutationFn: approveVendorKyc, onSuccess: invalidate });
  const rejectMut = useMutation({
    mutationFn: ({ id: i, reason }: { id: number; reason?: string }) =>
      rejectVendorKyc(i, reason),
    onSuccess: invalidate,
  });
  const suspendMut = useMutation({
    mutationFn: ({ id: i, reason }: { id: number; reason?: string }) =>
      suspendVendor(i, reason),
    onSuccess: invalidate,
  });
  const unsuspendMut = useMutation({ mutationFn: unsuspendVendor, onSuccess: invalidate });
  const saveMut = useMutation({
    mutationFn: (body: VendorPatchInput) => patchVendor(id, body),
    onSuccess: () => {
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['admin', 'vendors', id] });
    },
  });
  // Separate from saveMut: clearing the opt-in is an immediate ops action
  // (like unsuspend), not a field the draft/Save flow should be able to
  // leave half-applied alongside unrelated edits.
  const clearFixedPriceOptinMut = useMutation({
    mutationFn: () => patchVendor(id, { fixedPriceOptin: false }),
    onSuccess: () => {
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['admin', 'vendors', id] });
    },
  });

  if (isLoading || !draft) {
    return (
      <DetailLayout title="Loading…" backTo="/crew" backLabel="All crew">
        <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-5 text-[12.5px] text-[color:var(--bo-mute)]">
          Loading vendor…
        </div>
      </DetailLayout>
    );
  }
  if (error || !data) {
    return (
      <DetailLayout title="Not found" backTo="/crew" backLabel="All crew">
        <div className="rounded-xl border border-[#fecaca] bg-[#fff1f2] p-5 text-[12.5px] text-[#9f1239]">
          {error ? (error as Error).message : 'No vendor with that id.'}
        </div>
      </DetailLayout>
    );
  }

  const v = data;
  const display =
    v.businessName ?? [v.firstName, v.lastName].filter(Boolean).join(' ') ?? v.email;
  const moderationBusy =
    approveMut.isPending ||
    rejectMut.isPending ||
    suspendMut.isPending ||
    unsuspendMut.isPending;

  const patch = buildPatch(v, draft);
  const dirty = Object.keys(patch).length > 0;

  const setField = <K extends keyof VendorDraft>(k: K, val: VendorDraft[K]) =>
    setDraft((d) => (d ? { ...d, [k]: val } : d));

  return (
    <DetailLayout
      eyebrow="Operate · Crew"
      title={display}
      sub={`${v.email} · ${v.carsTotal} cars · ${v.bookingsTotal} bookings`}
      backTo="/crew"
      backLabel="All crew"
      actions={
        <>
          {v.verificationStatus !== 'approved' ? (
            <RowAction
              kind="primary"
              busy={moderationBusy}
              onClick={() => approveMut.mutate(v.id)}
            >
              Approve KYC
            </RowAction>
          ) : null}
          {v.verificationStatus !== 'rejected' ? (
            <RowAction
              kind="danger"
              busy={moderationBusy}
              onClick={() => {
                const reason = window.prompt('Rejection reason (optional):') ?? undefined;
                rejectMut.mutate({ id: v.id, reason });
              }}
            >
              Reject KYC
            </RowAction>
          ) : null}
          {v.status === 'suspended' ? (
            <RowAction busy={moderationBusy} onClick={() => unsuspendMut.mutate(v.id)}>
              Unsuspend
            </RowAction>
          ) : (
            <RowAction
              kind="danger"
              busy={moderationBusy}
              onClick={() => {
                const reason = window.prompt('Suspension reason (optional):') ?? undefined;
                suspendMut.mutate({ id: v.id, reason });
              }}
            >
              Suspend
            </RowAction>
          )}
          <StatusPill status={v.status} />
        </>
      }
    >
      <SaveBar
        dirty={dirty}
        saving={saveMut.isPending}
        success={saveMut.isSuccess && !dirty}
        error={saveMut.error}
        onReset={() => setDraft(vendorToDraft(v))}
        onSave={() => saveMut.mutate(patch)}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <DetailSection title="Identity">
          <AvatarPanel
            base="vendors"
            userId={v.id}
            display={display}
            joinedAt={v.createdAt}
            avatarUrl={resolveMediaUrl(v.avatar?.url)}
          />
          <div className="space-y-4">
            <TextField
              label="Email"
              required
              type="email"
              value={draft.email}
              onChange={(val) => setField('email', val)}
            />
            <div className="grid grid-cols-2 gap-3">
              <TextField
                label="First name"
                value={draft.firstName}
                onChange={(val) => setField('firstName', val)}
              />
              <TextField
                label="Last name"
                value={draft.lastName}
                onChange={(val) => setField('lastName', val)}
              />
            </div>
            <TextField
              label="Phone"
              type="tel"
              value={draft.phone}
              onChange={(val) => setField('phone', val)}
              placeholder="+44 ..."
            />
          </div>
        </DetailSection>

        <DetailSection title="Status & verification">
          <div className="space-y-4">
            <SelectField
              label="Account status"
              required
              value={draft.status}
              onChange={(val) => setField('status', val)}
              options={STATUS_OPTIONS}
              hint="`suspended` blocks the vendor from logging in or creating new cars."
            />
            <SelectField
              label="KYC verification"
              required
              value={draft.verificationStatus}
              onChange={(val) => setField('verificationStatus', val)}
              options={VERIFICATION_OPTIONS}
              hint="Crew only see the customer site as `approved`. The Approve / Reject KYC buttons above are shortcuts."
            />
          </div>
        </DetailSection>

        <DetailSection title="Business">
          <div className="space-y-4">
            <TextField
              label="Business name"
              value={draft.businessName}
              onChange={(val) => setField('businessName', val)}
              placeholder="Trading name"
            />
            <TextAreaField
              label="Business address"
              value={draft.businessAddress}
              onChange={(val) => setField('businessAddress', val)}
              rows={2}
            />
            <TextField
              label="Tax / VAT ID"
              value={draft.taxId}
              onChange={(val) => setField('taxId', val)}
              monospace
            />
          </div>
        </DetailSection>

        <DetailSection title="Plan & commission">
          <div className="space-y-4">
            <RelationshipPicker
              label="Plan"
              value={draft.planId}
              valueLabel={draft.planOption?.label ?? null}
              onChange={(planId, opt) => {
                setField('planId', planId);
                setField('planOption', opt ?? null);
              }}
              searchFn={async (q, signal) => {
                const res = await fetchAdminVendorPlans({ q, limit: 50 }, signal);
                return res.rows.map((r) => ({
                  id: r.id,
                  label: r.name,
                  sub: r.baseCommission != null ? `${r.baseCommission}% base` : null,
                }));
              }}
              hint="Sets fleet caps + base commission. Leave empty for ad-hoc / free vendors."
            />
            <div className="grid grid-cols-2 gap-3">
              <SelectField
                label="Commission override type"
                value={draft.commissionType}
                onChange={(val) => setField('commissionType', val)}
                options={COMMISSION_OPTIONS}
              />
              <NumberField
                label="Commission value"
                value={draft.commissionValue}
                onChange={(val) => setField('commissionValue', val)}
                min={0}
                step={0.5}
                suffix={draft.commissionType === 'percent' ? '%' : '€'}
                hint={
                  draft.commissionType === '' || draft.commissionType === 'default'
                    ? 'Override unused — falls back to plan / platform default.'
                    : draft.commissionType === 'percent'
                      ? 'Percent of GMV the platform retains.'
                      : 'Flat amount per booking the platform retains (€).'
                }
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <NumberField
                label="Vehicle markup override"
                value={draft.vehicleMarkupPct}
                onChange={(val) => setField('vehicleMarkupPct', val)}
                min={0}
                step={0.01}
                hint="Fraction added to this crew's listing prices (0.05 = 5%). Blank = global default."
              />
              <NumberField
                label="Platform fee override"
                value={draft.platformFeePct}
                onChange={(val) => setField('platformFeePct', val)}
                min={0}
                step={0.01}
                hint="Fraction taken as platform fee in the cart (0.2 = 20%). Blank = global default."
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              {/* Beside the platform fee because both are "what DriveCars
                  takes on this crew" — but they are not alternatives: the
                  platform fee is charged to the CUSTOMER on a booking-fee
                  partner, this is charged to the CREW on a supplier-commission
                  one. Blank is not zero here, and the hint says what blank
                  actually does, since a crew with no rate simply disappears
                  from those partners' results. */}
              <NumberField
                label="Supplier commission rate (%)"
                value={draft.supplierCommissionPct}
                onChange={(val) => setField('supplierCommissionPct', val)}
                min={0}
                max={1}
                step={0.01}
                hint="Applies when a partner sells this crew on a supplier-commission model. Fraction of the base the crew pays DriveCars (0.2 = 20%). Blank = no rate agreed, and the crew is hidden from those partners."
              />
            </div>
          </div>
        </DetailSection>

        <DetailSection title="Bid notifications">
          <div className="grid grid-cols-2 gap-3">
            <SelectField
              label="Notify by"
              value={draft.notificationChannel}
              onChange={(val) => setField('notificationChannel', val as VendorDraft['notificationChannel'])}
              options={[
                { value: 'email', label: 'Email' },
                { value: 'whatsapp', label: 'WhatsApp' },
                { value: 'both', label: 'Email + WhatsApp' },
              ]}
              hint="How this fleet is invited to bid on a booking."
            />
            <TextField
              label="WhatsApp number"
              value={draft.whatsappPhone}
              onChange={(val) => setField('whatsappPhone', val)}
              placeholder="+212600000000"
              hint="E.164 format. Required for WhatsApp bid invites."
            />
          </div>
        </DetailSection>

        <DetailSection title="Pickup location">
          <div className="grid grid-cols-2 gap-3">
            <NumberField
              label="Latitude"
              value={draft.pickupLatitude}
              onChange={(val) => setField('pickupLatitude', val)}
              placeholder="33.5731"
              hint="Pickup point shown on the results map. Tip: right-click the spot in Google Maps to copy the coordinates."
            />
            <NumberField
              label="Longitude"
              value={draft.pickupLongitude}
              onChange={(val) => setField('pickupLongitude', val)}
              placeholder="-7.5898"
            />
          </div>
        </DetailSection>

        <DetailSection title="Admin metadata">
          <div className="space-y-4">
            <BooleanField
              label="Flagged for review"
              hint="Internal flag — surfaces in the vendors list when filtering by `flagged`."
              checked={draft.flagged}
              onChange={(val) => setField('flagged', val)}
            />
            <VendorAutoAssignField
              vendor={v}
              checked={draft.autoAssign}
              onChange={(val) => setField('autoAssign', val)}
            />
            <TextField
              label="Tier"
              value={draft.tier}
              onChange={(val) => setField('tier', val)}
              placeholder="bronze / silver / gold"
              hint="Free-text categorisation; not enforced by the system."
            />
            <TextAreaField
              label="Internal notes"
              value={draft.notes}
              onChange={(val) => setField('notes', val)}
              rows={4}
              hint="Visible only to admins. Use for context other operators need (calls made, disputes, etc.)."
            />
          </div>
        </DetailSection>
      </div>

      <VendorLoginsSection vendorId={v.id} />

      <VendorSheetSection vendorId={v.id} />

      <VendorAvailabilitySection vendorId={v.id} />

      {TRANSFERS_UI_ENABLED ? <VendorTransfersSection vendorId={id} /> : null}

      <DetailSection title="Activity (read-only)">
        <DefRow label="Cars on platform">{v.carsTotal}</DefRow>
        <DefRow label="Total bookings">{v.bookingsTotal}</DefRow>
        <DefRow label="Lifetime payouts">
          {v.profile ? fmtMoney(v.profile.payoutsTotalCents) : '—'}
        </DefRow>
        <DefRow label="Avg response time">
          {v.profile?.responseTimeSeconds == null
            ? '—'
            : `${Math.round(v.profile.responseTimeSeconds / 60)} min`}
        </DefRow>
        <DefRow label="Fixed-price opt-in">
          <VendorFixedPriceField
            vendor={v}
            busy={clearFixedPriceOptinMut.isPending}
            onClear={() => clearFixedPriceOptinMut.mutate()}
          />
        </DefRow>
      </DetailSection>

      <VendorPayoutsSection vendorId={v.id} />

      {/* The other direction of this crew's money: what it OWES DriveCars on
          supplier-commission sales. Read-only; issuing lives on Finance. */}
      <VendorStatementsSection vendorId={v.id} />

      <EntityActivity entityType="users" entityId={v.id} title="Audit trail" />

      <SaveBar
        dirty={dirty}
        saving={saveMut.isPending}
        success={saveMut.isSuccess && !dirty}
        error={saveMut.error}
        onReset={() => setDraft(vendorToDraft(v))}
        onSave={() => saveMut.mutate(patch)}
      />
    </DetailLayout>
  );
}

// ─── Save bar (kept inline; identical to CarDetail's) ───────────────

function SaveBar({
  dirty,
  saving,
  success,
  error,
  onSave,
  onReset,
}: {
  dirty: boolean;
  saving: boolean;
  success: boolean;
  error: unknown;
  onSave: () => void;
  onReset: () => void;
}) {
  return (
    <div className="sticky top-0 z-10 -mx-1 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[color:var(--bo-line)] bg-white px-4 py-2.5 shadow-sm">
      <div className="text-[12.5px] text-[color:var(--bo-mute)]">
        {dirty ? (
          <span className="font-medium text-[color:var(--bo-ink)]">Unsaved changes</span>
        ) : success ? (
          <span className="text-[#166534]">Saved.</span>
        ) : (
          <span>Up to date.</span>
        )}
        {error ? (
          <span className="ms-3 text-[#9f1239]">Save failed: {(error as Error).message}</span>
        ) : null}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onReset}
          disabled={!dirty || saving}
          className="inline-flex h-9 items-center rounded-lg border border-[color:var(--bo-line)] px-3 text-[12.5px] font-medium text-[color:var(--bo-ink)] transition hover:border-[color:var(--bo-mute)] disabled:cursor-not-allowed disabled:opacity-50"
        >
          Reset
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={!dirty || saving}
          className="inline-flex h-9 items-center rounded-lg bg-[color:var(--bo-ink)] px-3.5 text-[12.5px] font-semibold text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}

// ─── Payouts ────────────────────────────────────────────────────────
//
// Read-only ledger of the vendor's payout history. Operators transition
// individual payouts via the global /payouts queue — clicking a row jumps
// to the corresponding PayoutDetail page.

const PAYOUT_COLS: DataTableColumn<AdminVendorPayoutRow>[] = [
  {
    key: 'reference',
    h: 'Reference',
    cell: (r) => <span className="font-mono text-[12px]">{r.reference}</span>,
  },
  {
    key: 'period',
    h: 'Period',
    cell: (r) => (
      <span className="text-[11.5px] text-[color:var(--bo-mute)]">
        {new Date(r.periodFrom).toLocaleDateString()} →{' '}
        {new Date(r.periodTo).toLocaleDateString()}
      </span>
    ),
  },
  {
    key: 'amount',
    h: 'Amount',
    right: true,
    cell: (r) => (
      <span className="tabular-nums font-semibold">
        {new Intl.NumberFormat('en-GB', {
          style: 'currency',
          currency: r.currency || 'EUR',
          maximumFractionDigits: 2,
        }).format(r.amount)}
      </span>
    ),
  },
  { key: 'status', h: 'Status', cell: (r) => <StatusPill status={r.status} /> },
  {
    key: 'paidAt',
    h: 'Paid',
    right: true,
    cell: (r) => (
      <span className="tabular-nums text-[11.5px] text-[color:var(--bo-mute)]">
        {r.paidAt ? new Date(r.paidAt).toLocaleDateString() : '—'}
      </span>
    ),
  },
  {
    key: 'createdAt',
    h: 'Created',
    right: true,
    cell: (r) => (
      <span className="tabular-nums text-[11.5px] text-[color:var(--bo-mute)]">
        {new Date(r.createdAt).toLocaleDateString()}
      </span>
    ),
  },
];

function VendorPayoutsSection({ vendorId }: { vendorId: number }) {
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'vendors', vendorId, 'payouts'],
    queryFn: ({ signal }) => fetchAdminVendorPayouts(vendorId, signal),
  });

  const rows = data?.rows ?? [];

  return (
    <DetailSection title={`Payouts (${rows.length})`}>
      {error ? (
        <div
          role="alert"
          className="mb-3 rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Couldn&apos;t load payouts: {(error as Error).message}
        </div>
      ) : null}
      <DataTable<AdminVendorPayoutRow>
        columns={PAYOUT_COLS}
        rows={rows}
        emptyLabel={isLoading ? 'Loading…' : 'No payouts yet.'}
        onRowClick={(r) =>
          void navigate({
            to: '/payouts/$id' as never,
            params: { id: String(r.id) } as never,
          })
        }
      />
    </DetailSection>
  );
}
